import { Box, Button, Chip, Divider, Stack, SxProps, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { CountryPicker } from "components/Facility/CountryPicker";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { NaicsPicker } from "components/NaicsPicker";
import { StatePicker } from "components/StatePicker";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  stateHasSubpartK,
  stateHasSubpartP,
} from "encamp-shared/src/waste/rcraSectionAvailability";
import { FacilityRcraProfileInput, RcraInfoLandType } from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useMemo, useState } from "react";
import { Controller, FormProvider, useFormContext, useFormState } from "react-hook-form";
import { hasCriticalIssues } from "util/forms";
import { getUserFacingMessage } from "utils/errorUtils";

import {
  useFacility,
  useFacilityRcraProfile,
  useRcraProfileIssues,
  useUpsertFacilityRcraProfile,
} from "../useFacility";
import {
  RCRA_EXEMPT_BOILER_ACTIVITY_LABELS,
  RCRA_LAND_TYPE_LABELS,
  RCRA_RECOGNIZED_TRADER_LABELS,
  RCRA_RECYCLER_ACTIVITY_LABELS,
  RCRA_SLAB_LABELS,
  RCRA_SUBPART_K_ENTITY_TYPE_LABELS,
  RCRA_SUBPART_P_ACTIVITY_LABELS,
  RCRA_TRANSPORTER_ACTIVITY_LABELS,
  RCRA_UNIVERSAL_WASTE_TYPE_LABELS,
  RCRA_USED_OIL_MARKETER_LABELS,
  RCRA_USED_OIL_PROCESSOR_LABELS,
  RCRA_USED_OIL_TRANSPORTER_LABELS,
  selectItemsFromLabels,
} from "./rcraProfileLabels";
import { RCRA_PROFILE_SECTION_ICON } from "./RcraProfileSection";
import { stripUnavailableSubparts } from "./stripUnavailableSubparts";
import { useFacilityRcraProfileInputValidation } from "./useFacilityRcraProfileInputValidation";

const gridSx: SxProps = {
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  columnGap: 2,
  rowGap: 0,
  "& .MuiFormHelperText-root.Mui-error": {
    mb: 1,
  },
};

const YES_NO_OPTIONS = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

type RcraProfileFormData = FacilityRcraProfileInput & {
  siteMailingSameAsLocation: boolean;
};

type FacilityData = NonNullable<NonNullable<ReturnType<typeof useFacility>["data"]>["facility"]>;

type RcraProfileData = NonNullable<
  NonNullable<
    NonNullable<ReturnType<typeof useFacilityRcraProfile>["data"]>["facility"]
  >["facilityRcraProfile"]
>;

function buildDefaultValues(profile: RcraProfileData | null): RcraProfileFormData {
  if (!profile) {
    return { siteMailingSameAsLocation: false, otherNaicsCodes: [] };
  }

  const mailingMatchesSite =
    Boolean(
      profile.rcraMailingStreetAddress1 || profile.rcraMailingCity || profile.rcraMailingState
    ) &&
    profile.rcraMailingStreetAddress1 === profile.rcraStreetAddress1 &&
    profile.rcraMailingStreetAddress2 === profile.rcraStreetAddress2 &&
    profile.rcraMailingCity === profile.rcraCity &&
    profile.rcraMailingState === profile.rcraState &&
    profile.rcraMailingZipCode === profile.rcraZipCode &&
    profile.rcraMailingCountry === profile.rcraCountry;

  return {
    rcraSiteName: profile.rcraSiteName ?? null,

    rcraStreetAddress1: profile.rcraStreetAddress1 ?? null,
    rcraStreetAddress2: profile.rcraStreetAddress2 ?? null,
    rcraCity: profile.rcraCity ?? null,
    rcraState: profile.rcraState ?? null,
    rcraZipCode: profile.rcraZipCode ?? null,
    rcraCountry: profile.rcraCountry ?? null,
    rcraCounty: profile.rcraCounty ?? null,
    rcraLatitude: profile.rcraLatitude ?? null,
    rcraLongitude: profile.rcraLongitude ?? null,
    useLatLongAsPrimary: profile.useLatLongAsPrimary ?? null,

    siteMailingSameAsLocation: mailingMatchesSite,
    rcraMailingStreetAddress1: profile.rcraMailingStreetAddress1 ?? null,
    rcraMailingStreetAddress2: profile.rcraMailingStreetAddress2 ?? null,
    rcraMailingCity: profile.rcraMailingCity ?? null,
    rcraMailingState: profile.rcraMailingState ?? null,
    rcraMailingZipCode: profile.rcraMailingZipCode ?? null,
    rcraMailingCountry: profile.rcraMailingCountry ?? null,

    landType: profile.landType ?? null,

    rcraNaicsCode: profile.rcraNaicsCode ?? null,
    otherNaicsCodes: profile.otherNaicsCodes ?? [],

    shortTermGenerator: profile.shortTermGenerator ?? null,
    shortTermGeneratorNotes: profile.shortTermGeneratorNotes ?? null,
    tsdActivity: profile.tsdActivity ?? null,
    offSiteReceipt: profile.offSiteReceipt ?? null,
    recyclerActivity: profile.recyclerActivity ?? null,
    exemptBoilerActivities: profile.exemptBoilerActivities ?? [],

    transporterActivity: profile.transporterActivity ?? null,
    undergroundInjectionControl: profile.undergroundInjectionControl ?? null,
    usImporterOfHazardousWaste: profile.usImporterOfHazardousWaste ?? null,
    recognizedTraderActivity: profile.recognizedTraderActivity ?? null,
    slabImporter: profile.slabImporter ?? null,

    lqhuwActivities: profile.lqhuwActivities ?? [],
    universalWasteDestFacility: profile.universalWasteDestFacility ?? null,

    usedOilTransporterActivities: profile.usedOilTransporterActivities ?? [],
    usedOilProcessorActivities: profile.usedOilProcessorActivities ?? [],
    offSpecUsedOilBurner: profile.offSpecUsedOilBurner ?? null,
    usedOilMarketerActivities: profile.usedOilMarketerActivities ?? [],

    subpartPActivity: profile.subpartPActivity ?? null,
    subpartPWithdrawal: profile.subpartPWithdrawal ?? null,

    subpartKEntityTypes: profile.subpartKEntityTypes ?? [],
    subpartKWithdrawal: profile.subpartKWithdrawal ?? null,

    manifestBroker: profile.manifestBroker ?? null,
  };
}

export function EditRcraProfileDialog({ onClose }: { onClose: () => void }) {
  const alerts = useAlerts();
  const { data } = useFacility();
  const facility = data?.facility;
  const { data: rcraData } = useFacilityRcraProfile();
  const profile = rcraData?.facility?.facilityRcraProfile ?? null;

  const [upsertRcraProfile, { loading: saving }] = useUpsertFacilityRcraProfile();

  const defaultValues = useMemo(() => buildDefaultValues(profile), [profile]);

  const persistedIssues = useRcraProfileIssues();
  const validate = useFacilityRcraProfileInputValidation(facility?.id);
  const validator = useCallback(
    async (formData: RcraProfileFormData) => {
      const { siteMailingSameAsLocation, ...rest } = formData;
      const input: FacilityRcraProfileInput = {
        ...rest,
        rcraLatitude:
          formData.rcraLatitude == null ? null : safeParseFloat(String(formData.rcraLatitude)),
        rcraLongitude:
          formData.rcraLongitude == null ? null : safeParseFloat(String(formData.rcraLongitude)),
      };
      if (siteMailingSameAsLocation) {
        input.rcraMailingStreetAddress1 = formData.rcraStreetAddress1 ?? null;
        input.rcraMailingStreetAddress2 = formData.rcraStreetAddress2 ?? null;
        input.rcraMailingCity = formData.rcraCity ?? null;
        input.rcraMailingState = formData.rcraState ?? null;
        input.rcraMailingZipCode = formData.rcraZipCode ?? null;
        input.rcraMailingCountry = formData.rcraCountry ?? null;
      }
      return validate(stripUnavailableSubparts(input));
    },
    [validate]
  );
  const form = useValidatingForm<RcraProfileFormData>(defaultValues, persistedIssues, validator);

  const { handleSubmit, issues } = form;
  const { isDirty } = useFormState({ control: form.control });

  // Subpart K (Academic Lab) and Subpart P (Pharmaceutical) are only collected
  // on some states' 8700-12 forms; gate their sections on the site state.
  const rcraState = form.watch("rcraState");
  const showSubpartK = stateHasSubpartK(rcraState);
  const showSubpartP = stateHasSubpartP(rcraState);

  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const onSubmit = useCallback(
    async (formData: RcraProfileFormData) => {
      if (!facility) return;

      const { siteMailingSameAsLocation, ...rest } = formData;

      const input: FacilityRcraProfileInput = {
        ...rest,
        rcraLatitude:
          formData.rcraLatitude == null ? null : safeParseFloat(String(formData.rcraLatitude)),
        rcraLongitude:
          formData.rcraLongitude == null ? null : safeParseFloat(String(formData.rcraLongitude)),
      };

      if (siteMailingSameAsLocation) {
        input.rcraMailingStreetAddress1 = formData.rcraStreetAddress1 ?? null;
        input.rcraMailingStreetAddress2 = formData.rcraStreetAddress2 ?? null;
        input.rcraMailingCity = formData.rcraCity ?? null;
        input.rcraMailingState = formData.rcraState ?? null;
        input.rcraMailingZipCode = formData.rcraZipCode ?? null;
        input.rcraMailingCountry = formData.rcraCountry ?? null;
      }

      try {
        await upsertRcraProfile({
          variables: { facilityId: facility.id, input: stripUnavailableSubparts(input) },
        });
        alerts.success("RCRA profile saved.");
        onClose();
      } catch (err) {
        alerts.error(getUserFacingMessage(err) ?? "Error saving RCRA profile.");
        console.info("Error saving RCRA profile.", err);
      }
    },
    [facility, upsertRcraProfile, alerts, onClose]
  );

  return (
    <>
      <Dialog
        open
        title={
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h6">Edit</Typography>
            <Typography variant="h6">{RCRA_PROFILE_SECTION_ICON}</Typography>
            <Typography variant="h6">RCRA Profile</Typography>
          </Stack>
        }
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        primaryActionButtonProps={{
          loading: saving,
          disabled: !isDirty || hasCriticalIssues(issues),
          onClick: () => handleSubmit(onSubmit)(),
        }}
        additionalPrimaryActionElements={[
          <IssueListButton issues={issues} key="issue-list-button" />,
        ]}
      >
        <FormProvider {...form}>
          <form>
            <Stack spacing={0}>
              <SiteNameSection />
              <Divider sx={{ my: 2 }} />
              <SiteLocationSection facility={facility} />
              <Divider sx={{ my: 2 }} />
              <SiteMailingAddressSection />
              <Divider sx={{ my: 2 }} />
              <LandTypeSection />
              <Divider sx={{ my: 2 }} />
              <NaicsSection />
              <Divider sx={{ my: 2 }} />
              <GeneratorActivitySection />
              <Divider sx={{ my: 2 }} />
              <WasteActivitySection />
              <Divider sx={{ my: 2 }} />
              <LqhuwSection />
              <Divider sx={{ my: 2 }} />
              <UsedOilSection />
              {showSubpartP && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <SubpartPSection />
                </>
              )}
              {showSubpartK && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <SubpartKSection />
                </>
              )}
              <Divider sx={{ my: 2 }} />
              <ManifestBrokerSection />
            </Stack>
          </form>
        </FormProvider>
      </Dialog>
      <ConfirmDialog
        open={showConfirmClose}
        onClose={() => setShowConfirmClose(false)}
        onConfirm={onClose}
        header="You have unsaved changes. Are you sure you want to close?"
        confirmText="Close without saving"
      />
    </>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <Typography variant="subtitle2" fontWeight={600} mb={2}>
      {label}
    </Typography>
  );
}

function SiteNameSection() {
  const { control } = useFormContext<RcraProfileFormData>();
  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Site Name" />
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <FormTextField name="rcraSiteName" label="RCRA Site Name" control={control} />
      </Box>
    </Box>
  );
}

function SiteLocationSection({ facility }: { facility: FacilityData | null | undefined }) {
  const { control, setValue } = useFormContext<RcraProfileFormData>();
  const { control: untypedControl } = useFormContext();

  const copyFromFacility = useCallback(() => {
    if (!facility) return;
    const opts = { shouldDirty: true, shouldValidate: true };
    setValue("rcraStreetAddress1", facility.streetAddress1 ?? null, opts);
    setValue("rcraStreetAddress2", facility.streetAddress2 ?? null, opts);
    setValue("rcraCity", facility.city ?? null, opts);
    setValue("rcraState", facility.state ?? null, opts);
    setValue("rcraZipCode", facility.zip ?? null, opts);
    setValue("rcraCountry", facility.country ?? null, opts);
    setValue("rcraCounty", facility.county ?? null, opts);
    setValue("rcraLatitude", facility.latitude ?? null, opts);
    setValue("rcraLongitude", facility.longitude ?? null, opts);
  }, [facility, setValue]);

  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Site Location" />
      </Box>
      {facility && (
        <Box sx={{ gridColumn: "span 12" }}>
          <Button variant="text" size="small" onClick={copyFromFacility} sx={{ mb: 1 }}>
            Copy from facility address
          </Button>
        </Box>
      )}
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField name="rcraStreetAddress1" label="Street Address" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField name="rcraStreetAddress2" label="Street Address 2" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField name="rcraCity" label="City" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <StatePicker name="rcraState" countryFieldName="rcraCountry" control={untypedControl} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField name="rcraZipCode" label="Zip Code" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField name="rcraCounty" label="County" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <CountryPicker name="rcraCountry" control={untypedControl} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField name="rcraLatitude" label="Latitude" control={control} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField name="rcraLongitude" label="Longitude" control={control} />
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <RadioGroupField
          label="Use lat/long as primary address?"
          name="useLatLongAsPrimary"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
    </Box>
  );
}

function SiteMailingAddressSection() {
  const { control, watch } = useFormContext<RcraProfileFormData>();
  const { control: untypedControl } = useFormContext();
  const sameAsLocation = watch("siteMailingSameAsLocation");

  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Site Mailing Address" />
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <RadioGroupField
          label="Same as site location?"
          name="siteMailingSameAsLocation"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
      {!sameAsLocation && (
        <>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormTextField
              name="rcraMailingStreetAddress1"
              label="Mailing Street Address"
              control={control}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormTextField
              name="rcraMailingStreetAddress2"
              label="Mailing Street Address 2"
              control={control}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
            <FormTextField name="rcraMailingCity" label="Mailing City" control={control} />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
            <StatePicker
              name="rcraMailingState"
              countryFieldName="rcraMailingCountry"
              control={untypedControl}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
            <FormTextField name="rcraMailingZipCode" label="Mailing Zip Code" control={control} />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
            <CountryPicker name="rcraMailingCountry" control={untypedControl} />
          </Box>
        </>
      )}
    </Box>
  );
}

function LandTypeSection() {
  const { control } = useFormContext<RcraProfileFormData>();
  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Site Land Type" />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="Land Type"
          name="landType"
          control={control}
          selectItems={selectItemsFromLabels<RcraInfoLandType>(RCRA_LAND_TYPE_LABELS)}
        />
      </Box>
    </Box>
  );
}

function NaicsSection() {
  const { control } = useFormContext<RcraProfileFormData>();
  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="NAICS Codes" />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <Controller
          name="rcraNaicsCode"
          control={control}
          render={({ field, fieldState }) => (
            <NaicsPicker
              {...fieldState}
              value={field.value ?? null}
              onChange={field.onChange}
              disabled={false}
            />
          )}
        />
      </Box>
      <Box sx={{ gridColumn: "span 12", mt: 2 }}>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Additional NAICS Codes
        </Typography>
        <Controller
          name="otherNaicsCodes"
          control={control}
          render={({ field, fieldState }) => {
            const codes = field.value ?? [];
            const add = (code: number | null) => {
              if (code === null || codes.includes(code)) return;
              field.onChange([...codes, code]);
            };
            const remove = (code: number) => {
              field.onChange(codes.filter((c) => c !== code));
            };
            return (
              <Stack spacing={1}>
                {codes.length > 0 && (
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {codes.map((code) => (
                      <Chip
                        key={code}
                        label={code.toString()}
                        size="small"
                        onDelete={() => remove(code)}
                      />
                    ))}
                  </Stack>
                )}
                <NaicsPicker {...fieldState} value={null} onChange={add} disabled={false} />
              </Stack>
            );
          }}
        />
      </Box>
    </Box>
  );
}

function GeneratorActivitySection() {
  const { control } = useFormContext<RcraProfileFormData>();
  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Hazardous Waste Activities" />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <RadioGroupField
          label="Short-Term Generator?"
          name="shortTermGenerator"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <FormTextField
          name="shortTermGeneratorNotes"
          label="Short-Term Generator Notes"
          control={control}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <RadioGroupField
          label="Treater, Storer, or Disposer of Hazardous Waste (TSD)?"
          name="tsdActivity"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <RadioGroupField
          label="Receives Hazardous Waste from Off-Site?"
          name="offSiteReceipt"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="Recycler Activity"
          name="recyclerActivity"
          control={control}
          selectItems={selectItemsFromLabels(RCRA_RECYCLER_ACTIVITY_LABELS)}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="Exempt Boiler / Industrial Furnace"
          name="exemptBoilerActivities"
          control={control}
          selectItems={selectItemsFromLabels(RCRA_EXEMPT_BOILER_ACTIVITY_LABELS)}
          selectProps={{ multiple: true }}
          clearable={false}
        />
      </Box>
    </Box>
  );
}

function WasteActivitySection() {
  const { control } = useFormContext<RcraProfileFormData>();
  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Waste Activities" />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="Transporter Activity"
          name="transporterActivity"
          control={control}
          selectItems={selectItemsFromLabels(RCRA_TRANSPORTER_ACTIVITY_LABELS)}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <RadioGroupField
          label="Underground Injection Control?"
          name="undergroundInjectionControl"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <RadioGroupField
          label="US Importer of Hazardous Waste?"
          name="usImporterOfHazardousWaste"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="Recognized Trader Activity"
          name="recognizedTraderActivity"
          control={control}
          selectItems={selectItemsFromLabels(RCRA_RECOGNIZED_TRADER_LABELS)}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="SLAB Activity"
          name="slabImporter"
          control={control}
          selectItems={selectItemsFromLabels(RCRA_SLAB_LABELS)}
        />
      </Box>
    </Box>
  );
}

function LqhuwSection() {
  const { control } = useFormContext<RcraProfileFormData>();
  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Large Quantity Handler of Universal Waste" />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="Universal Waste Types"
          name="lqhuwActivities"
          control={control}
          selectItems={selectItemsFromLabels(RCRA_UNIVERSAL_WASTE_TYPE_LABELS)}
          selectProps={{ multiple: true }}
          clearable={false}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <RadioGroupField
          label="Universal Waste Destination Facility?"
          name="universalWasteDestFacility"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
    </Box>
  );
}

function UsedOilSection() {
  const { control } = useFormContext<RcraProfileFormData>();
  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Used Oil" />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="Used Oil Transporter Activities"
          name="usedOilTransporterActivities"
          control={control}
          selectItems={selectItemsFromLabels(RCRA_USED_OIL_TRANSPORTER_LABELS)}
          selectProps={{ multiple: true }}
          clearable={false}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="Used Oil Processor Activities"
          name="usedOilProcessorActivities"
          control={control}
          selectItems={selectItemsFromLabels(RCRA_USED_OIL_PROCESSOR_LABELS)}
          selectProps={{ multiple: true }}
          clearable={false}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <RadioGroupField
          label="Off-Specification Used Oil Burner?"
          name="offSpecUsedOilBurner"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="Used Oil Marketer Activities"
          name="usedOilMarketerActivities"
          control={control}
          selectItems={selectItemsFromLabels(RCRA_USED_OIL_MARKETER_LABELS)}
          selectProps={{ multiple: true }}
          clearable={false}
        />
      </Box>
    </Box>
  );
}

function SubpartPSection() {
  const { control } = useFormContext<RcraProfileFormData>();
  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Pharmaceutical (Subpart P)" />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="Subpart P Activity"
          name="subpartPActivity"
          control={control}
          selectItems={selectItemsFromLabels(RCRA_SUBPART_P_ACTIVITY_LABELS)}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <RadioGroupField
          label="Subpart P Withdrawal?"
          name="subpartPWithdrawal"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
    </Box>
  );
}

function SubpartKSection() {
  const { control } = useFormContext<RcraProfileFormData>();
  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Academic Lab (Subpart K)" />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormSelect
          label="Subpart K Entity Types"
          name="subpartKEntityTypes"
          control={control}
          selectItems={selectItemsFromLabels(RCRA_SUBPART_K_ENTITY_TYPE_LABELS)}
          selectProps={{ multiple: true }}
          clearable={false}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <RadioGroupField
          label="Subpart K Withdrawal?"
          name="subpartKWithdrawal"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
    </Box>
  );
}

function ManifestBrokerSection() {
  const { control } = useFormContext<RcraProfileFormData>();
  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Electronic Manifest Broker" />
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <RadioGroupField
          label="Notifying as an electronic manifest broker?"
          name="manifestBroker"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
    </Box>
  );
}
